import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../services/auth';
import { getExamResults } from '../../services/content';

const ProfilePage = () => {
  const { profile, subscription, getSubscriptionStatus, getUserLevel, hasActiveSubscription } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    phoneNumber: ''
  });
  const [examResults, setExamResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (profile) {
      setFormData({
        displayName: profile.displayName || '',
        phoneNumber: profile.phoneNumber || ''
      });
    }
  }, [profile]);

  useEffect(() => {
    const fetchExamResults = async () => {
      if (profile && hasActiveSubscription()) {
        try {
          const results = await getExamResults(profile.uid, getUserLevel());
          setExamResults(results);
        } catch (error) {
          console.error('Error fetching exam results:', error);
        }
      }
    };

    fetchExamResults();
  }, [profile, getUserLevel, hasActiveSubscription]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      await updateUserProfile(profile.uid, formData);
      setEditMode(false);
      setMessage('Profile updated successfully!');
    } catch (error) {
      setMessage('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getLevelInfo = () => {
    const levels = {
      grade7: { name: 'Grade 7', price: 'K500' },
      form2: { name: 'Form 2', price: 'K1000' },
      grade12: { name: 'Grade 12', price: 'K1500' }
    };
    return levels[getUserLevel()] || { name: 'Unknown', price: 'K0' };
  };

  const getSubscriptionBadge = () => {
    const status = getSubscriptionStatus();
    const badges = {
      unpaid: { color: 'gray', text: 'Unpaid', description: 'Complete payment to activate' },
      payment_pending: { color: 'yellow', text: 'Payment Pending', description: 'Your payment is being reviewed' },
      active: { color: 'green', text: 'Active', description: 'Full access to all content' },
      expired: { color: 'red', text: 'Expired', description: 'Renew your subscription to continue' }
    };
    
    return badges[status] || badges.unpaid;
  };

  const levelInfo = getLevelInfo();
  const subscriptionBadge = getSubscriptionBadge();

  return (
    <div className="profile-page">
      <div className="container">
        <div className="page-header">
          <h1>My Profile</h1>
          <p>Manage your account and track your progress</p>
        </div>

        <div className="profile-content">
          <div className="profile-grid">
            {/* Personal Information */}
            <div className="profile-card">
              <div className="card-header">
                <h2>Personal Information</h2>
                {!editMode && (
                  <button 
                    onClick={() => setEditMode(true)}
                    className="btn btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                )}
              </div>

              {editMode ? (
                <form onSubmit={handleSubmit} className="profile-form">
                  <div className="form-group">
                    <label htmlFor="displayName">Full Name</label>
                    <input
                      type="text"
                      id="displayName"
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phoneNumber">Phone Number</label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditMode(false)}
                      className="btn btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>

                  {message && (
                    <div className={`message ${message.includes('success') ? 'success' : 'error'}`}>
                      {message}
                    </div>
                  )}
                </form>
              ) : (
                <div className="profile-info">
                  <div className="info-item">
                    <label>Full Name:</label>
                    <span>{profile?.displayName}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{profile?.email}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone Number:</label>
                    <span>{profile?.phoneNumber || 'Not provided'}</span>
                  </div>
                  <div className="info-item">
                    <label>Account Type:</label>
                    <span>{profile?.role === 'admin' ? 'Administrator' : 'Student'}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Subscription Information */}
            <div className="profile-card">
              <div className="card-header">
                <h2>Subscription Details</h2>
              </div>

              <div className="subscription-info">
                <div className="subscription-status">
                  <div className={`status-badge ${subscriptionBadge.color}`}>
                    {subscriptionBadge.text}
                  </div>
                  <p className="status-description">{subscriptionBadge.description}</p>
                </div>

                <div className="subscription-details">
                  <div className="detail-item">
                    <label>Education Level:</label>
                    <span>{levelInfo.name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Subscription Price:</label>
                    <span>{levelInfo.price}/year</span>
                  </div>
                  
                  {subscription && (
                    <>
                      <div className="detail-item">
                        <label>Start Date:</label>
                        <span>{subscription.startDate?.toDate?.()?.toLocaleDateString()}</span>
                      </div>
                      {subscription.endDate && (
                        <div className="detail-item">
                          <label>End Date:</label>
                          <span>{subscription.endDate.toDate().toLocaleDateString()}</span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {getSubscriptionStatus() !== 'active' && (
                  <div className="subscription-actions">
                    <button className="btn btn-primary">
                      {getSubscriptionStatus() === 'unpaid' ? 'Make Payment' : 'Renew Subscription'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Academic Progress */}
          {hasActiveSubscription() && (
            <div className="profile-card full-width">
              <div className="card-header">
                <h2>Academic Progress</h2>
              </div>

              <div className="academic-progress">
                <div className="progress-summary">
                  <div className="summary-item">
                    <h3>{examResults.length}</h3>
                    <p>Exams Completed</p>
                  </div>
                  <div className="summary-item">
                    <h3>
                      {examResults.length > 0 
                        ? Math.round(examResults.reduce((acc, result) => acc + result.percentage, 0) / examResults.length)
                        : 0
                      }%
                    </h3>
                    <p>Average Score</p>
                  </div>
                  <div className="summary-item">
                    <h3>
                      {examResults.filter(result => result.passed).length}
                    </h3>
                    <p>Exams Passed</p>
                  </div>
                </div>

                {examResults.length > 0 && (
                  <div className="recent-results">
                    <h4>Recent Exam Results</h4>
                    <div className="results-list">
                      {examResults.slice(0, 5).map((result) => (
                        <div key={result.id} className="result-item">
                          <div className="result-info">
                            <h5>Exam #{result.examId.slice(-6)}</h5>
                            <p>{result.completedAt?.toDate?.()?.toLocaleDateString()}</p>
                          </div>
                          <div className="result-score">
                            <span className={`score ${result.passed ? 'passed' : 'failed'}`}>
                              {result.percentage}%
                            </span>
                            <span className="status">
                              {result.passed ? '✅ Passed' : '❌ Failed'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {examResults.length === 0 && (
                  <div className="no-results">
                    <p>No exam results yet. Start practicing to see your progress!</p>
                    <button className="btn btn-primary">Take Practice Exam</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
